import {
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import { Box, Paper, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import AlertDialog from '../../../components/AlertDialog/';
import {
  getTicketPriorities,
  getTicketStatuses,
} from '../../../constants/ticket';
import { useSnackbar } from '../../../hooks/useSnackbar';
import { pointAPI } from '../../../services/PointService';
import { serviceCatalogAPI } from '../../../services/ServiceCatalogService';
import { ticketAPI } from '../../../services/TicketService';
import { userAPI } from '../../../services/UserService';
import { warehouseAPI } from '../../../services/WarehouseService';
import { selectCurrentUser } from '../../../store/reducers/AuthSlice';
import { IItem } from '../../../types/IItem';
import { IServiceItem } from '../../../types/IServiceItem';
import { IWarehouseItem } from '../../../types/IWarehouse';
import { CatalogServices } from './CatalogServices';
import { CustomServices } from './CustomServices';
import { PartsList } from './PartsList';
import { TicketNoteAndPaymentFields } from './TicketNoteAndPaymentFields';
import { TopFields } from './TopFields';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const Content: FC<{
  services?: IServiceItem[];
  ticket?: IItem;
  setStatus: (val: string) => void;
  savedInventoryItemsForCurrentTicket?: IWarehouseItem[];
  refetchSavedInventoryItemsForCurrentTicket: () => void;
}> = ({
  services: savedServices,
  ticket,
  setStatus,
  savedInventoryItemsForCurrentTicket,
  refetchSavedInventoryItemsForCurrentTicket,
}) => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const ticketStatuses = getTicketStatuses(t);

  const ticketPriorities = getTicketPriorities(t);

  const currentUser = useSelector(selectCurrentUser);

  const isOwner = currentUser?.userInfo.role === 'owner';

  const { savedCatalogServices, savedCustomServices } = useMemo(() => {
    const savedCatalogServices = [];
    const savedCustomServices = [];

    if (!savedServices) {
      return {
        savedCatalogServices: [],
        savedCustomServices: [],
      };
    }

    for (const service of savedServices) {
      if (service.type === 'catalog') {
        savedCatalogServices.push(service);
      } else if (service.type === 'custom') {
        savedCustomServices.push(service);
      }
    }

    return { savedCatalogServices, savedCustomServices };
  }, [savedServices]);

  const {
    data: responseServices,
    isError: servicesError,
    isLoading: isLoadingServices,
  } = serviceCatalogAPI.useGetServiceItemsByCompanyIdQuery(undefined);

  const serviceItems = responseServices?.data;

  const [
    delayedRequestGetWarehouseItemsByCompanyId,
    setDelayedRequestGetWarehouseItemsByCompanyId,
  ] = useState(false);

  const [servicesTotal, setServicesTotal] = useState(0);

  const [last_part_payment, setLastPartPayment] = useState(0);

  const [totalSum, setTotalSum] = useState(0);

  const [partsTotal, setPartsTotal] = useState(0);

  useEffect(() => {
    if (savedInventoryItemsForCurrentTicket) {
      setDelayedRequestGetWarehouseItemsByCompanyId(false);
    }
  }, [savedInventoryItemsForCurrentTicket]);

  const {
    data: availableInventoryItemsForCurrentTicket,
    refetch: refetchAvailableInventoryItemsForCurrentTicket,
  } = warehouseAPI.useGetWarehouseItemsByCompanyIdQuery(undefined, {
    skip: delayedRequestGetWarehouseItemsByCompanyId,
  });

  const [partsFields, setPartsFields] = useState<string[]>([]);

  const [catalogServicesFields, setCatalogServicesFields] = useState<string[]>(
    [],
  );

  const [customServicesFields, setCustomServicesFields] = useState<string[]>(
    [],
  );

  useEffect(() => {
    if (savedInventoryItemsForCurrentTicket) {
      setPartsFields([]);

      savedInventoryItemsForCurrentTicket.forEach((item, index) => {
        setValue(`parts.${index}.id`, item.id);

        setValue(
          `parts.${index}.used_count_this_ticket`,
          item.used_count_this_ticket,
        );

        setValue(`parts.${index}.price_at_use`, item.price_at_use);

        setPartsFields((prev) => [...prev, uuidv4()]);
      });
    }
  }, [savedInventoryItemsForCurrentTicket]);

  useEffect(() => {
    if (savedCatalogServices) {
      setCatalogServicesFields([]);

      savedCatalogServices.forEach((item: IServiceItem, index: number) => {
        setValue(`servicesCatalog.${index}.id`, item.id);

        setValue(`servicesCatalog.${index}.service_id`, item.service_id);

        setValue(
          `servicesCatalog.${index}.price_at_use`,
          Number(item.price_at_use),
        );

        setCatalogServicesFields((prev) => [...prev, uuidv4()]);
      });
    }
  }, [savedCatalogServices]);

  useEffect(() => {
    if (savedCustomServices) {
      setCustomServicesFields([]);

      savedCustomServices.forEach((item: IServiceItem, index: number) => {
        setValue(`servicesCustom.${index}.id`, item.id);

        setValue(`servicesCustom.${index}.type`, item.type);

        setValue(`servicesCustom.${index}.service_id`, null);

        setValue(`servicesCustom.${index}.custom_name`, item.custom_name);

        setValue(
          `servicesCustom.${index}.price_at_use`,
          Number(item.price_at_use),
        );

        setCustomServicesFields((prev) => [...prev, uuidv4()]);
      });
    }
  }, [savedCustomServices]);

  const [openDeleteAlertDialog, setOpenDeleteAlertDialog] = useState(false);

  const { data: points, isLoading: isLoadingPoints } =
    pointAPI.useGetPointsQuery('');

  const { data: workers } = userAPI.useGetAllUsersQuery('');

  const [updateTicket, { error: errorUpdateTicket }] =
    ticketAPI.useUpdateTicketMutation();

  const [deleteTicket] = ticketAPI.useDeleteTicketMutation();

  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    resetField,
    formState: { errors },
  } = useForm<IItem>({
    defaultValues: {
      last_part_payment: 0,
      discount: 0,
      note: '',
      point_id: '',
      assigned_at: null,
      guarantee_till: null,
    },
  });

  const { remove: removeField } = useFieldArray({
    control,
    name: 'parts',
  });

  const { remove: removeFieldServiceCatalog, append: appendServiceCatalog } =
    useFieldArray({
      control,
      name: 'servicesCatalog',
    });

  const { remove: removeFieldServiceCustom, append: appendServiceCustom } =
    useFieldArray({
      control,
      name: 'servicesCustom',
    });

  const status = watch('status');

  useEffect(() => {
    setStatus(status);
  }, [status]);

  useEffect(() => {
    if (ticket) {
      setValue('status', ticket.status);
      setValue('priority', ticket.priority);
      setValue('note', ticket.note);
      setValue('last_part_payment', ticket.last_part_payment);
      setValue('discount', ticket.discount);
      setValue('point_id', ticket.point_id);
      setValue('assigned_at', ticket.assigned_at);
      setValue('guarantee_till', ticket.guarantee_till);
    }
  }, [ticket]);
  useEffect(() => {
    if (status !== 'paid') {
      setValue('guarantee_till', null);
    }
  }, [status]);

  const hasSavedInventoryItemsForCurrentTicket = useMemo(() => {
    return (
      savedInventoryItemsForCurrentTicket &&
      savedInventoryItemsForCurrentTicket.length > 0
    );
  }, [savedInventoryItemsForCurrentTicket]);

  const watchedPartsValues = useWatch({ control, name: 'parts' }) || [];

  const servicesCatalogValue =
    useWatch({ control, name: 'servicesCatalog' }) || [];

  const servicesCustomValue =
    useWatch({ control, name: 'servicesCustom' }) || [];

  const watchedDiscountValue = useWatch({ control, name: 'discount' }) || 0;

  useEffect(() => {
    const sumServices = [
      ...servicesCatalogValue,
      ...servicesCustomValue,
    ].reduce((acc: any, service: any) => {
      if (!service.price_at_use) return acc;

      acc = acc + Number(service.price_at_use);
      return acc;
    }, 0);

    const sumParts = watchedPartsValues.reduce((acc: any, part: any) => {
      if (!part.price_at_use || !part.used_count_this_ticket) return acc;
      acc =
        acc + Number(part.price_at_use) * Number(part.used_count_this_ticket);
      return acc;
    }, 0);

    const deposit = ticket ? Number(ticket.paid) : 0;

    const payment =
      sumParts + sumServices - Number(watchedDiscountValue) - deposit;

    const totalSumValue = sumParts + sumServices - Number(watchedDiscountValue);

    const totalSum = totalSumValue >= 0 ? totalSumValue : 0;

    const last_part_payment = payment >= 0 ? payment : 0;

    setValue('last_part_payment', last_part_payment);

    setValue('servicesTotal', sumServices);

    setServicesTotal(sumServices);

    setPartsTotal(sumParts);

    setLastPartPayment(last_part_payment);

    setTotalSum(totalSum);
  }, [
    servicesCustomValue,
    servicesCatalogValue,
    watchedPartsValues,
    watchedDiscountValue,
  ]);

  const warehouseDataFiltered = useMemo(() => {
    if (!availableInventoryItemsForCurrentTicket?.data) return [];

    return availableInventoryItemsForCurrentTicket.data.filter((item) => {
      return !servicesCatalogValue?.some((part) => part?.id === item.id);
    });
  }, [servicesCatalogValue, availableInventoryItemsForCurrentTicket]);

  const dataFromError: any =
    errorUpdateTicket && 'data' in errorUpdateTicket
      ? (errorUpdateTicket.data as any)?.message
      : undefined;

  const onSubmit: SubmitHandler<IItem> = async (args) => {
    showSnackbar('', false, false);

    if (args.assigned_at === 'None') args.assigned_at = null;

    try {
      await updateTicket({
        ...args,
        ticket_id: ticket?.ticket_id,
      }).unwrap();

      await refetchAvailableInventoryItemsForCurrentTicket();

      await refetchSavedInventoryItemsForCurrentTicket();

      showSnackbar(t('update_successful'), false);
    } catch (err: any) {
      const message = err?.data?.message || 'Something went wrong';
      showSnackbar(message, true);
    }
  };

  const workersList = useMemo(() => {
    return [
      {
        user_id: null,
        name: 'None',
      },
      ...(workers ?? []),
    ];
  }, [workers]);

  const handleDeleteTicket = useCallback(async () => {
    setOpenDeleteAlertDialog(false);

    try {
      await deleteTicket(ticket?.ticket_id).unwrap();
      navigate(`/tickets`);
    } catch (error: any) {
      showSnackbar(error?.data?.error, true);
    }
  }, [ticket?.ticket_id, deleteTicket, navigate]);

  const canAddMoreParts = useMemo(() => {
    return (
      !availableInventoryItemsForCurrentTicket?.data ||
      availableInventoryItemsForCurrentTicket.data.length > partsFields.length
    );
  }, [availableInventoryItemsForCurrentTicket, partsFields]);
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h6" gutterBottom margin={4}>
        {t('editTicket.title')} #{ticket?.ticket_number}
      </Typography>

      <Stack spacing={3} sx={{ textAlign: 'left', marginBottom: 4 }}>
        {ticket && (
          <TopFields
            t={t}
            control={control}
            ticket={ticket}
            status={status}
            ticketStatuses={ticketStatuses}
            ticketPriorities={ticketPriorities}
            points={points}
            isLoadingPoints={isLoadingPoints}
            workersList={workersList}
            Item={Item}
          />
        )}

        <PartsList
          savedInventoryItemsForCurrentTicket={
            savedInventoryItemsForCurrentTicket
          }
          availableInventoryItemsForCurrentTicket={
            availableInventoryItemsForCurrentTicket
          }
          partsFields={partsFields}
          setPartsFields={setPartsFields}
          getValues={getValues}
          control={control}
          setValue={setValue}
          resetField={resetField}
          errors={errors}
          removeField={removeField}
          t={t}
          warehouseDataFiltered={warehouseDataFiltered}
          canAddMoreParts={canAddMoreParts}
          setDelayedRequestGetWarehouseItemsByCompanyId={
            setDelayedRequestGetWarehouseItemsByCompanyId
          }
        />

        <CatalogServices
          servicesFields={catalogServicesFields}
          setServicesFields={setCatalogServicesFields}
          removeField={removeFieldServiceCatalog}
          getValues={getValues}
          control={control}
          setValue={setValue}
          resetField={resetField}
          errors={errors}
          serviceItems={serviceItems}
          servicesLoadError={servicesError}
          isLoading={isLoadingServices}
          t={t}
          append={appendServiceCatalog}
        />

        <CustomServices
          servicesFields={customServicesFields}
          setServicesFields={setCustomServicesFields}
          removeField={removeFieldServiceCustom}
          getValues={getValues}
          control={control}
          setValue={setValue}
          resetField={resetField}
          errors={errors}
          servicesLoadError={servicesError}
          isLoading={isLoadingServices}
          t={t}
          append={appendServiceCustom}
        />

        <TicketNoteAndPaymentFields
          control={control}
          errors={errors}
          t={t}
          paid={ticket ? ticket.paid : 0}
          servicesTotal={servicesTotal}
          partsTotal={partsTotal}
          last_part_payment={last_part_payment}
          totalSum={totalSum}
        />

        <SnackbarComponent />

        <Box
          sx={{
            marginTop: '40px !important',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          {isOwner && (
            <>
              <Button
                color="error"
                size="small"
                variant="text"
                onClick={() => {
                  setOpenDeleteAlertDialog(true);
                }}
              >
                {t('editTicket.delete_ticket')}
              </Button>
              <AlertDialog
                handleClose={() => setOpenDeleteAlertDialog(false)}
                handleClickOk={handleDeleteTicket}
                isOpen={openDeleteAlertDialog}
                showSubmitBtn={!hasSavedInventoryItemsForCurrentTicket}
                title={
                  !hasSavedInventoryItemsForCurrentTicket
                    ? t('editTicket.alert_delete_title')
                    : t('editTicket.alert_delete_title_with_parts')
                }
              />
            </>
          )}

          <LoadingButton size="large" type="submit" variant="contained">
            {t('editTicket.update_ticket')}
          </LoadingButton>
        </Box>
      </Stack>
    </form>
  );
};

export default Content;
